export interface GamelibProduct {
  productId: string
  name: string
  price: number
  chargeGem: number
  freeGem: number
  increaseGem: number
  bonusGem: number
  totalGem: number
  startTime: string | null
  endTime: string | null
  productEndTime: string
  limitedCount: number
  formattedPrice: string
  currency: string
  campaignType: number
  campaignMode: CampaignMode
  consumable: number
  providerId: number

  realityCoinCampaign: RealityCoinCampaign
  realityCoinBonus: number
}

export const ProviderIdTypes = {
  Stripe: 9,
  PayPay: 13,
} as const
type ProviderIdTypes = (typeof ProviderIdTypes)[keyof typeof ProviderIdTypes]

// キャンペーン商品かどうかを判定する
export function isCampaignProduct(product: GamelibProduct) {
  return (
    product.campaignType !== campaignTypes.Default &&
    product.campaignMode !== campaignModes.NotCampaign
  )
}

// 一度限りの購入商品かどうかを判定する
export function isOncePurchaseProduct(product: GamelibProduct) {
  return (
    product.campaignType === campaignTypes.Default && product.consumable === 0
  )
}

// 購入回数制限のある割引商品かどうかを判定する
export function isLimitedCountAndGemIncreaseProduct(product: GamelibProduct) {
  return (
    product.campaignMode === campaignModes.LimitedCountGemIncrease ||
    product.campaignMode === campaignModes.LimitedCountSale
  )
}

// マンスリーコインかどうかを確認する
export function isMonthlyCoinProduct(product: GamelibProduct) {
  const patterns = [
    /.+\.monthly\..+/, // 任意の文字列 + ".monthly." + 任意の文字列
    /.+\.monthly$/, // 任意の文字列 + ".monthly"（行末で終わる）
    /^monthly\..+/, // "monthly." + 任意の文字列（行頭から始まる）
  ]

  return patterns.some((pattern) => pattern.test(product.productId))
}

// 「5:商品が購入回数制限の有償ジェム増量」or「6:購入回数制限の価格割引」の場合は購入回数制限が0ではないかをチェックする
// ※購入回数制限が0の場合は、default商品として扱うので、キャンペーン商品として扱わない
export function isLimitedCampaignWithNoRemaining(product: GamelibProduct) {
  const isLimitedCountCampaign = isLimitedCountAndGemIncreaseProduct(product)
  return isLimitedCountCampaign && product.limitedCount === 0
}

// PayPay決済かどうかを判定する
export function isPurchasePayPay(product: GamelibProduct) {
  return product.providerId === ProviderIdTypes.PayPay
}

export interface GroupeList {
  campaign: ProductGroup[]
  oncePurchase: ProductGroup[]
  monthlyCoin: ProductGroup[]
  default: ProductGroup[]
}

export interface ProductGroup {
  group: string
  products: GamelibProduct[]
}

export interface RealityCoinCampaign {
  exists: boolean
  productName: string
}

// https://extra-confluence.gree-office.net/display/200055GAM/GL02-05-01-p16+-+Product+List+API
export const campaignTypes = {
  Default: 0, // default
  CampaignWithDefault: 1, // campaign適用(default有)
  CampaignOnlyNoDefault: 2, // campaign専用またはcampaign期間限定(default無)
}
export type CampaignType = (typeof campaignTypes)[keyof typeof campaignTypes]

// https://extra-confluence.gree-office.net/display/200055GAM/GL02-05-01-p16+-+Product+List+API
export const campaignModes = {
  NotCampaign: 0, // キャンペーン未適用
  GemIncrease: 2, // 有償・無償ジェム増量
  Sale: 4, // 価格割引
  LimitedCountGemIncrease: 5, // 購入回数制限の有償ジェム増量
  LimitedCountSale: 6, // 購入回数制限の価格割引
}
export type CampaignMode = (typeof campaignModes)[keyof typeof campaignModes]

export interface GamelibTransaction {
  transactionType: TransactionType
  date: string
  reason: string
  balanceChangeFree: number
  balanceChangeCharge: number
}

export const transactionTypes = {
  Spend: 1, // 消費
  ChargeGain: 2, // 有償獲得
  FreeGain: 3, // 無償獲得
} as const
export type TransactionType =
  (typeof transactionTypes)[keyof typeof transactionTypes]

export interface GamelibPaymentMethod {
  id: string
  card: GamelibCard
  primary: boolean
  payment_method_type: gamelib.webpay.model.PaymentMethodType
}

interface GamelibCard {
  brand: string
  expMonth: number
  expYear: number
  last4: string
}

export interface GamelibKonbiniPayment {
  konbiniPaymentMinPrice: number
  isKonbiniPaymentAvailableIPRegion: boolean
  isKonbiniPaymentAvailableProduct: boolean
}

export function getCardInfo(method: GamelibPaymentMethod) {
  const { expMonth, expYear, last4 } = method.card
  return `************${last4} ${expMonth}/${expYear.toString().substr(2)}`
}

export function getPaymentMethodImg(method: GamelibPaymentMethod): string {
  let paymentMethodImg: string = ''

  const paymentMethodType = method.payment_method_type
  if (paymentMethodType == 'apple_pay') {
    paymentMethodImg = '../img/payment_method_icons/ApplePay.png'
    return paymentMethodImg
  }

  if (paymentMethodType == 'google_pay') {
    paymentMethodImg = '../img/payment_method_icons/GooglePay.png'
    return paymentMethodImg
  }

  const brand = method.card.brand

  // INFO:Cartes Bancairesの場合、ブランド提携しているVisaまたはMastercardで登録される。
  switch (brand) {
    case 'visa':
      paymentMethodImg = '../img/payment_method_icons/Visa.png'
      break
    case 'mastercard':
      paymentMethodImg = '../img/payment_method_icons/Mastercard.png'
      break
    case 'amex':
      paymentMethodImg = '../img/payment_method_icons/amex.png'
      break
    case 'discover':
      paymentMethodImg = '../img/payment_method_icons/discover.png'
      break
    case 'diners':
      paymentMethodImg = '../img/payment_method_icons/diners.png'
      break
    case 'jcb':
      paymentMethodImg = '../img/payment_method_icons/JCB.png'
      break
  }

  return paymentMethodImg
}
